import type { Maybe } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { registerEnum } from '@adornis/baseql/metadata/register.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';
import { UserStammdaten } from '../../entities/stammdaten.js';
import type { RegistrationStammdatenPlugin } from '../plugin-types.js';

export const csjensblanksRegistrationStammdatenPlugin: RegistrationStammdatenPlugin = {
  id: 'registration::stammdaten::csjensblank',
  type: 'registration::stammdaten',
  disableCustomFormfields: true,
  createStammdaten: async ({ userID, institutionID, createInDB } = {}) => {
    const stammdaten = new CsjensblanksStammdaten({ userID, institutionID });
    if (createInDB) await stammdaten.create();
    return stammdaten;
  },
  getFields: () => {
    return Object.keys(CsjensblanksStammdaten.fields).filter(
      it => !['_id', '_class', 'userID', 'institutionID'].includes(it),
    );
  },
  classType: () => CsjensblanksStammdaten,
};

export enum CsjensblanksGenderEnum {
  CSJENSBLANK_MALE = 'CSJENSBLANK_MALE',
  CSJENSBLANK_FEMALE = 'CSJENSBLANK_FEMALE',
  CSJENSBLANK_DIVERSE = 'CSJENSBLANK_DIVERSE',
  CSJENSBLANK_NOT_SPECIFIED = 'CSJENSBLANK_NOT_SPECIFIED',
}
registerEnum({ enum: CsjensblanksGenderEnum, name: 'CsjensblanksGenderEnum' });

export enum CsjensblanksUniEnum {
  CSJENSBLANK_FRA = 'CSJENSBLANK_FRA',
  CSJENSBLANK_GU = 'CSJENSBLANK_GU',
  CSJENSBLANK_HDA = 'CSJENSBLANK_HDA',
  CSJENSBLANK_HSRM = 'CSJENSBLANK_HSRM',
  CSJENSBLANK_CSJENSBLANK = 'CSJENSBLANK_CSJENSBLANK',
  CSJENSBLANK_JGU = 'CSJENSBLANK_JGU',
  CSJENSBLANK_ANDERE = 'CSJENSBLANK_ANDERE',
}
registerEnum({ enum: CsjensblanksUniEnum, name: 'CsjensblanksUniEnum' });

export const CsjensblanksDepartments = [
  'FB 01 | Rechtswissenschaft',
  'FB 02 | Wirtschaftswissenschaften',
  'FB 03 | Gesellschaftswissenschaften',
  'FB 04 | Erziehungswissenschaften',
  'FB 05 | Psychologie und Sportwissenschaften',
  'FB 06 | Evangelische Theologie',
  'FB 07 | Katholische Theologie',
  'FB 08 | Philosophie und Geschichtswissenschaften',
  'FB 09 | Sprach- und Kulturwissenschaften',
  'FB 10 | Neuere Philologien',
  'FB 11 | Geowissenschaften / Geographie',
  'FB 12 | Informatik und Mathematik',
  'FB 13 | Physik',
  'FB 14 | Biochemie, Chemie und Pharmazie',
  'FB 15 | Biowissenschaften',
  'FB 16 | Medizin',
  'Sonstiges ',
];

export enum CsjensblanksDesiredDegreeEnum {
  CSJENSBLANK_BACHELOR = 'CSJENSBLANK_BACHELOR',
  CSJENSBLANK_MASTER = 'CSJENSBLANK_MASTER',
  CSJENSBLANK_STAATSEXAMEN_LEHRAMT = 'CSJENSBLANK_STAATSEXAMEN_LEHRAMT',
  CSJENSBLANK_STAATSEXAMEN_MEDIZIN_PHARMAZIE = 'CSJENSBLANK_STAATSEXAMEN_MEDIZIN_PHARMAZIE',
  CSJENSBLANK_STAATSEXAMEN_JURA = 'CSJENSBLANK_STAATSEXAMEN_JURA',
  CSJENSBLANK_PROMOTION = 'CSJENSBLANK_PROMOTION',
  CSJENSBLANK_SONSTIGE = 'CSJENSBLANK_SONSTIGE',
}
registerEnum({ enum: CsjensblanksDesiredDegreeEnum, name: 'CsjensblanksDesiredDegreeEnum' });

@Entity()
export class CsjensblanksStammdaten extends UserStammdaten {
  static override _class = 'CsjensblanksStammdaten';

  override getTranslationNamespace() {
    return 'csjensblank';
  }

  @validate(nonOptional())
  @Field(type => String)
  matriklNr: Maybe<string>;

  // 1. Persönliche Angaben
  // Geschlecht: männlich/ weiblich/ divers/ ohne Angabe
  @validate(nonOptional())
  @Field(type => CsjensblanksGenderEnum)
  gender: Maybe<CsjensblanksGenderEnum>;

  @validate(nonOptional())
  @Field(type => DateTime)
  dateOfBirth: Maybe<DateTime>;

  // Staatsangehörigkeit: (Drop Down alle Länder)
  @validate(nonOptional())
  @Field(type => String)
  nationality: Maybe<string>;

  // Geburtsland: (Drop Down alle Länder)
  @validate(nonOptional())
  @Field(type => String)
  countryOfBirth: Maybe<string>;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => String, { default: value => value ?? 'DE' })
  uniQualificationCountry: Maybe<string>;

  // Fachsemester:
  @validate(nonOptional())
  @Field(type => String)
  semester: Maybe<string>;

  // Studiengang:
  @validate(nonOptional())
  @Field(type => String)
  courseOfStudy: Maybe<string>;

  // Fachbereich:
  @validate(nonOptional())
  @Field(type => String)
  department: Maybe<string>;

  // Angestrebter Abschluss:
  @validate(nonOptional())
  @Field(type => CsjensblanksDesiredDegreeEnum)
  desiredDegree: Maybe<CsjensblanksDesiredDegreeEnum>;
}
